import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, throwError} from 'rxjs';
import { catchError, map, mergeMap, switchMap, delay, retry} from 'rxjs/operators';

import * as AIActions from './ai.actions'; // Import the actions 
import { AIRequests } from './ai.requests'; // Import the requests 

import * as Objects from '@core/objects';


@Injectable()
export class AIEffects {


    private readonly maxRetries = 90;
    private readonly delayTime = 2000; // 2 seconds --> total 180 seconds (3 minutes)

    constructor(private actions$: Actions,
        private requests: AIRequests) { }

    /**
     * send warmup call
     */
    warm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AIActions.WarmUp),
            mergeMap((action) =>
                this.requests.warm$().pipe(
                    map((response: Objects.AIAnswer) => AIActions.WarmUpSuccess({ response })),
                    catchError(error => of(AIActions.WarmUpFailure({ error })))
                )
            )
        )
    );
    /**
     * Send new question effect
     */
    ask$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AIActions.SendQuestion),
            mergeMap((action) =>
                this.requests.send$(action.question).pipe(
                    map((response: Objects.AIQuestionResponse) => AIActions.SendQuestionSuccess({ response })),
                    catchError(error => of(AIActions.SendQuestionFailure({ error })))
                )
            )
        )
    );

    askSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AIActions.SendQuestionSuccess),
            mergeMap((action) => 
                of(AIActions.FetchAnswer(action.response)).pipe(
                    delay(this.delayTime) // Delay the FetchAnswer action by 2 seconds
                )
            )
        )
    );

    /**
     * Get answer from token handling a 425 not ready response code to wait until ready
     */

    // Effect to handle the fetch action
    fetchAnswer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AIActions.FetchAnswer), 
            mergeMap((action) =>
                this.requests.fetchAnswer$(action.token).pipe(
                    retry({
                        delay: (error, retryCount) => {
                            if (error.status === 425 && retryCount < this.maxRetries) {
                                // Retry up to 3 times with a 1-second delay
                                return of(error).pipe(delay(this.delayTime));
                            }
                            return throwError(() => error);
                        }
                    }),
                    switchMap((response) => {

                        // Success: Dispatch success action with the answer
                        return of(AIActions.FetchAnswerSuccess({ token: action.token, response: response }));
                    }),
                    catchError(error => {
                        // Handle errors such as network issues
                        return of(AIActions.FetchAnswerFailure({ token: action.token, error: error.message }));
                    })
                )
            )
        )
    )


}